import { formatInTimeZone } from "date-fns-tz";
import {
    IAiSessionNoteFieldReview,
    IAiSessionNoteDraft,
    IAiSessionNoteReview,
    AiSessionNoteType,
    IAiSessionNoteWriterContext,
    NoteStatus,
    NoteTypes,
} from "../types/notes.types";

export const AI_SESSION_NOTE_TYPES: AiSessionNoteType[] = [
    NoteTypes.INDIVIDUAL_SOAP_NOTE,
    NoteTypes.FAMILY_SOAP_NOTE,
    NoteTypes.INITIAL_SOAP_NOTE,
    NoteTypes.FAMILY_INITIAL_SOAP_NOTE,
];

export function isAiSessionNotesEnabled(): boolean {
    return import.meta.env.VITE_AI_SESSION_NOTES_ENABLED === "true";
}

export const AI_GENERATED_NOTE_FIELDS = [
    "emotional_behavioural_symptoms_demonstrated",
    "subjective",
    "objective",
    "assessment",
    "client_response",
    "plan",
    "prognosis",
    "progress_one",
    "progress_two",
    "progress_three",
] as const;

export type AiGeneratedNoteField = typeof AI_GENERATED_NOTE_FIELDS[number];

const aiWriterFieldLabels: Record<AiGeneratedNoteField, string> = {
    emotional_behavioural_symptoms_demonstrated:
        "Emotional / behavioural symptoms",
    subjective: "Subjective",
    objective: "Objective",
    assessment: "Assessment",
    client_response: "Client response",
    plan: "Plan",
    prognosis: "Prognosis",
    progress_one: "Treatment goal 1 progress",
    progress_two: "Treatment goal 2 progress",
    progress_three: "Treatment goal 3 progress",
};

export const AI_WRITER_CUE_FIELDS = [
    "emotional_behavioural_symptoms_demonstrated",
    "subjective",
    "objective",
    "assessment",
    "client_response",
    "plan",
    "prognosis",
    "progress_one",
    "progress_two",
    "progress_three",
] as const;

export type AiWriterCueField = typeof AI_WRITER_CUE_FIELDS[number];
export type AiWriterFieldCue = "ai_generated" | "needs_input";
export type AiWriterValue = string | string[];
export type AiWriterDraftSnapshot = Partial<
    Record<AiWriterCueField, AiWriterValue>
>;

const aiReviewNarrativeFieldByFormField = {
    emotional_behavioural_symptoms_demonstrated:
        "emotional_behavioral_symptoms",
    causing: "functional_effect",
    intention_for_session: "session_focus",
    subjective: "subjective",
    objective: "objective",
    assessment: "assessment",
    client_response: "response",
    plan: "plan",
    prognosis: "prognosis",
} as const;

const aiReviewInitialFieldByFormField = {
    risk_and_safety_language: "risk_and_safety",
    new_risk_concerns_details: "new_risk_concerns",
    additional_client_reported_information_post_intake:
        "post_intake_information",
} as const;

export const AI_REVIEW_FORM_FIELDS = [
    ...(Object.keys(aiReviewNarrativeFieldByFormField) as Array<
        keyof typeof aiReviewNarrativeFieldByFormField
    >),
    ...(Object.keys(aiReviewInitialFieldByFormField) as Array<
        keyof typeof aiReviewInitialFieldByFormField
    >),
    "goal_one",
    "progress_one",
    "goal_two",
    "progress_two",
    "goal_three",
    "progress_three",
] as const;

export type AiReviewFormField = typeof AI_REVIEW_FORM_FIELDS[number];

export function getAiReviewFieldFeedbackId(field: AiReviewFormField): string {
    return `ai-session-note-review-field-${field}`;
}

const AI_REVIEW_PLACEHOLDER_TEXT = new Set([
    "none",
    "n a",
    "not applicable",
    "no suggestion",
    "no suggestions",
]);
const AI_REVIEW_PLACEHOLDER_SEPARATOR =
    /[\s\u0021-\u002f\u003a-\u0040\u005b-\u0060\u007b-\u007e\u2000-\u206f\u3000-\u303f]+/g;

export function isMeaningfulAiReviewText(value: string): boolean {
    const normalized = value
        .normalize("NFKC")
        .trim()
        .toLowerCase()
        .replace(AI_REVIEW_PLACEHOLDER_SEPARATOR, " ")
        .trim();
    return normalized.length > 0 && !AI_REVIEW_PLACEHOLDER_TEXT.has(normalized);
}

export function filterMeaningfulAiReviewText(values: string[]): string[] {
    return values.filter(isMeaningfulAiReviewText);
}

export function getAiReviewForFormField(
    review: IAiSessionNoteReview,
    field: AiReviewFormField
): IAiSessionNoteFieldReview | undefined {
    if (field in aiReviewNarrativeFieldByFormField) {
        const canonicalField =
            aiReviewNarrativeFieldByFormField[
                field as keyof typeof aiReviewNarrativeFieldByFormField
            ];
        return review.field_reviews[canonicalField];
    }
    if (field in aiReviewInitialFieldByFormField) {
        const canonicalField =
            aiReviewInitialFieldByFormField[
                field as keyof typeof aiReviewInitialFieldByFormField
            ];
        return review.initial_field_reviews?.[canonicalField];
    }
    const [reviewType, ordinal] = field.split("_");
    const goalReview = review.goal_reviews.find(
        ({ goal_id }) => goal_id === `goal_${ordinal}`
    );
    return reviewType === "goal" ? goalReview?.goal : goalReview?.progress;
}

export function getAiReviewFieldsNeedingAttention(
    review: IAiSessionNoteReview
): AiReviewFormField[] {
    return AI_REVIEW_FORM_FIELDS.filter((field) => {
        const fieldReview = getAiReviewForFormField(review, field);
        return (
            fieldReview?.status === "missing" ||
            fieldReview?.status === "needs_improvement"
        );
    });
}

function stringValues(value: unknown): string[] {
    const values = Array.isArray(value) ? value : [value];
    return values.flatMap((entry) =>
        typeof entry === "string" && entry.trim() ? [entry.trim()] : []
    );
}

export function buildAiWriterDraftSnapshot(
    draft: IAiSessionNoteDraft
): AiWriterDraftSnapshot {
    const snapshot: AiWriterDraftSnapshot = {};
    AI_WRITER_CUE_FIELDS.forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(draft.note_content, field)) {
            const value = draft.note_content[field];
            if (typeof value === "string") {
                snapshot[field] = value.trim() ? value : "";
            } else if (Array.isArray(value)) {
                snapshot[field] = stringValues(value);
            }
        }
    });
    return snapshot;
}

export function getAiWriterDraftUpdates(
    draft: IAiSessionNoteDraft
): Partial<Record<AiGeneratedNoteField, AiWriterValue>> {
    const updates: Partial<Record<AiGeneratedNoteField, AiWriterValue>> = {};
    AI_GENERATED_NOTE_FIELDS.forEach((field) => {
        const value = draft.note_content[field];
        if (typeof value === "string" && value.trim()) {
            updates[field] = value;
        } else if (Array.isArray(value)) {
            const normalizedValues = stringValues(value);
            if (normalizedValues.length > 0) {
                updates[field] = normalizedValues;
            }
        }
    });
    return updates;
}

export function getAiWriterFieldCue(
    snapshot: AiWriterDraftSnapshot | null,
    field: AiWriterCueField,
    currentValue: unknown
): AiWriterFieldCue | null {
    if (!snapshot || !Object.prototype.hasOwnProperty.call(snapshot, field)) {
        return null;
    }
    const generatedValue = snapshot[field] ?? "";
    if (Array.isArray(generatedValue)) {
        const normalizedCurrentValue = stringValues(currentValue);
        if (
            JSON.stringify(normalizedCurrentValue) !==
            JSON.stringify(generatedValue)
        ) {
            return null;
        }
        return generatedValue.length > 0 ? "ai_generated" : "needs_input";
    }
    const normalizedCurrentValue =
        typeof currentValue === "string" ? currentValue : "";
    if (normalizedCurrentValue !== generatedValue) return null;
    return generatedValue.trim() ? "ai_generated" : "needs_input";
}

export interface AiWriterFormValues {
    diagnosis_codes: string[];
    secondary_diagnosis: string[];
    tertiary_diagnosis: string[];
    additional_diagnosis_one: string[];
    additional_diagnosis_two: string[];
    goal_one: string;
    goal_two: string;
    goal_three: string;
}

const reviewLabels: Record<string, string> = {
    emotional_behavioural_symptoms_demonstrated:
        "Emotional / Behavioural symptoms demonstrated",
    causing: "Causing",
    intention_for_session: "Intention for Session",
    client_response: "Client Response",
    risk_and_safety_language: "Risk and safety language",
    new_risk_concerns_details: "New risk concerns details",
    additional_client_reported_information_post_intake:
        "Additional client reported information (Post-Intake)",
    emotional_behavioral_symptoms: "Emotional and behavioral symptoms",
    functional_effect: "Functional effect",
    session_focus: "Session focus",
    subjective: "Subjective",
    objective: "Objective",
    assessment: "Assessment",
    response: "Client response",
    plan: "Plan",
    prognosis: "Prognosis",
    risk_and_safety: "Risk and safety",
    new_risk_concerns: "New risk concerns",
    post_intake_information: "Post-intake information",
    completeness: "Completeness",
    consistency: "Consistency",
    medical_necessity: "Medical necessity",
    diagnosis_support: "Diagnosis support",
    goal_progress: "Goal progress",
    intervention_and_response: "Intervention and response",
    plan_quality: "Plan quality",
    goal_one: "Goal one",
    goal_two: "Goal two",
    goal_three: "Goal three",
};

export function isAiSessionNoteType(
    noteType?: NoteTypes
): noteType is AiSessionNoteType {
    return Boolean(
        noteType &&
            AI_SESSION_NOTE_TYPES.includes(noteType as AiSessionNoteType)
    );
}

export function getAiWriterPreviousNoteTypes(
    noteType?: NoteTypes
): AiSessionNoteType[] {
    const family =
        noteType === NoteTypes.FAMILY_SOAP_NOTE ||
        noteType === NoteTypes.FAMILY_INITIAL_SOAP_NOTE;
    return family
        ? [NoteTypes.FAMILY_SOAP_NOTE, NoteTypes.FAMILY_INITIAL_SOAP_NOTE]
        : [NoteTypes.INDIVIDUAL_SOAP_NOTE, NoteTypes.INITIAL_SOAP_NOTE];
}

export function canReviewSessionNoteWithAi({
    hasPermission,
    noteType,
    hasSavedContent,
    isManual,
    isArchived,
}: {
    hasPermission: boolean;
    noteType?: NoteTypes;
    hasSavedContent: boolean;
    isManual: boolean;
    isArchived: boolean;
}): boolean {
    return (
        isAiSessionNotesEnabled() &&
        hasPermission &&
        isAiSessionNoteType(noteType) &&
        hasSavedContent &&
        !isManual &&
        !isArchived
    );
}

export function getPopulatedAiWriterFields(
    values: Record<string, unknown>
): AiGeneratedNoteField[] {
    return AI_GENERATED_NOTE_FIELDS.filter((field) => {
        const value = values[field];
        return typeof value === "string"
            ? value.trim().length > 0
            : stringValues(value).length > 0;
    });
}

export function formatAiWriterFieldLabel(field: AiGeneratedNoteField): string {
    return aiWriterFieldLabels[field];
}

export function hasWriterContext(
    context: IAiSessionNoteWriterContext
): boolean {
    return [context.goal_one, context.goal_two, context.goal_three].some(
        (goal) => typeof goal === "string" && goal.trim().length > 0
    );
}

function optionalString(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    return trimmed;
}

function boundedContinuityString(value: unknown): string | undefined {
    const text = optionalString(value);
    return text && text.length <= 4_000 ? text : undefined;
}

export function buildAiWriterContext(
    values: Record<string, unknown>
): IAiSessionNoteWriterContext {
    return {
        diagnosis_codes: Array.from(
            new Set(
                [
                    values.diagnosis_codes,
                    values.secondary_diagnosis,
                    values.tertiary_diagnosis,
                    values.additional_diagnosis_one,
                    values.additional_diagnosis_two,
                ].flatMap(stringValues)
            )
        ).slice(0, 5),
        goal_one:
            optionalString(values.goal_one) ??
            optionalString(values.treatment_goal_one),
        goal_two:
            optionalString(values.goal_two) ??
            optionalString(values.treatment_goal_two),
        goal_three:
            optionalString(values.goal_three) ??
            optionalString(values.treatment_goal_three),
        risk_and_safety_language: optionalString(
            values.risk_and_safety_language
        ),
        new_risk_concerns_details: optionalString(
            values.new_risk_concerns_details
        ),
        additional_client_reported_information_post_intake: optionalString(
            values.additional_client_reported_information_post_intake
        ),
    };
}

interface AiWriterPreviousNote {
    note_id: string;
    status: string;
    date_of_service: string;
    is_archived: boolean;
    note_type?: string;
    note_content: Record<string, unknown>;
}

interface AiWriterCurrentNote {
    note_id: string;
    date_of_service: string;
}

export function formatAiWriterDateOfService(value: string | null | undefined) {
    if (!value) return undefined;

    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) return undefined;
    return formatInTimeZone(date, "America/New_York", "yyyy-MM-dd");
}

export function resolveAiWriterContext(
    currentContext: IAiSessionNoteWriterContext,
    previousNotes: AiWriterPreviousNote[],
    currentNote: AiWriterCurrentNote
): IAiSessionNoteWriterContext {
    const currentDate = formatAiWriterDateOfService(
        currentNote.date_of_service
    );
    if (currentDate === undefined) return currentContext;

    const eligibleStatuses = new Set<string>([
        NoteStatus.PENDING_CODE_REVIEW,
        NoteStatus.CODED,
    ]);
    const eligibleNotes = previousNotes
        .filter((note) => {
            const noteDate = formatAiWriterDateOfService(note.date_of_service);
            return (
                note.note_id !== currentNote.note_id &&
                !note.is_archived &&
                eligibleStatuses.has(note.status) &&
                noteDate !== undefined &&
                noteDate < currentDate
            );
        })
        .sort((left, right) =>
            (
                formatAiWriterDateOfService(right.date_of_service) ?? ""
            ).localeCompare(
                formatAiWriterDateOfService(left.date_of_service) ?? ""
            )
        );
    const previousContext = eligibleNotes
        .map((note) => buildAiWriterContext(note.note_content))
        .find(hasWriterContext);
    const hasCurrentGoal = Boolean(
        currentContext.goal_one ||
            currentContext.goal_two ||
            currentContext.goal_three
    );
    const context =
        previousContext && !hasCurrentGoal
            ? {
                  ...currentContext,
                  diagnosis_codes: currentContext.diagnosis_codes,
                  goal_one: previousContext.goal_one,
                  goal_two: previousContext.goal_two,
                  goal_three: previousContext.goal_three,
              }
            : currentContext;
    const previousSessionNote = eligibleNotes.find((note) =>
        isAiSessionNoteType(note.note_type as NoteTypes)
    );
    if (!previousSessionNote) return context;
    const previousNoteDate = formatAiWriterDateOfService(
        previousSessionNote.date_of_service
    );
    if (!previousNoteDate) return context;

    const recentGoalProgress = ["one", "two", "three"].flatMap((ordinal) => {
        const goalId = `goal_${ordinal}` as
            | "goal_one"
            | "goal_two"
            | "goal_three";
        const previousGoal = optionalString(
            previousSessionNote.note_content[goalId]
        );
        const progress = boundedContinuityString(
            previousSessionNote.note_content[`progress_${ordinal}`]
        );
        if (!progress || !previousGoal || previousGoal !== context[goalId]) {
            return [];
        }
        return [
            {
                goal_id: goalId,
                goal: previousGoal,
                progress,
            },
        ];
    });
    if (recentGoalProgress.length === 0) {
        return context;
    }

    return {
        ...context,
        continuity_context: {
            source_note_id: previousSessionNote.note_id,
            previous_note_date: previousNoteDate,
            recent_goal_progress: recentGoalProgress,
        },
    };
}

export function mergeAiWriterInterventionSelections(
    currentValues: unknown,
    generatedValues: unknown
): string[] {
    return Array.from(
        new Set([
            ...stringValues(currentValues),
            ...stringValues(generatedValues),
        ])
    );
}

export function buildAiWriterFormValues(
    context: IAiSessionNoteWriterContext
): AiWriterFormValues {
    const [primary, secondary, tertiary, additionalOne, additionalTwo] =
        context.diagnosis_codes;

    return {
        diagnosis_codes: primary ? [primary] : [],
        secondary_diagnosis: secondary ? [secondary] : [],
        tertiary_diagnosis: tertiary ? [tertiary] : [],
        additional_diagnosis_one: additionalOne ? [additionalOne] : [],
        additional_diagnosis_two: additionalTwo ? [additionalTwo] : [],
        goal_one: context.goal_one ?? "",
        goal_two: context.goal_two ?? "",
        goal_three: context.goal_three ?? "",
    };
}

export function formatAiReviewLabel(value: string): string {
    const withoutPrefix = value
        .replace(/^narrative\./, "")
        .replace(/^initial_context\./, "")
        .replace(/^goal:/, "");
    return (
        reviewLabels[withoutPrefix] ??
        withoutPrefix
            .split("_")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
    );
}

export function formatAiReviewStatus(value: string): string {
    const labels: Record<string, string> = {
        adequate: "Adequate",
        needs_improvement: "Needs improvement",
        missing: "Missing",
        not_applicable: "Not applicable",
        high: "High priority",
        medium: "Medium priority",
        low: "Low priority",
    };
    return labels[value] ?? formatAiReviewLabel(value);
}
